import type { IRequestOptions } from './types';

class TimeoutError extends Error {
  constructor(message = '요청 시간이 초과되었습니다.') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export async function fetchWithTimeout(
  url: string,
  options: IRequestOptions,
  timeout = 10000
): Promise<Response> {
  const {
    timeout: timeoutMs,
    keepAbortSignalAfterResponse,
    signal: externalSignal,
    ...restOptions
  } = options;
  const effectiveTimeout = timeoutMs ?? timeout;

  if (effectiveTimeout <= 0) {
    return fetch(url, {
      ...restOptions,
      signal: externalSignal,
    });
  }

  const controller = new AbortController();
  let isTimeoutAbort = false;
  const timeoutId = setTimeout(() => {
    isTimeoutAbort = true;
    controller.abort();
  }, effectiveTimeout);

  const onAbort = () => controller.abort();
  externalSignal?.addEventListener('abort', onAbort, { once: true });

  try {
    const response = await fetch(url, {
      ...restOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!keepAbortSignalAfterResponse) {
      externalSignal?.removeEventListener('abort', onAbort);
    }
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', onAbort);
    if (
      isTimeoutAbort &&
      error instanceof Error &&
      error.name === 'AbortError'
    ) {
      throw new TimeoutError();
    }
    throw error;
  }
}
